import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import type { AgentChatMessage, AgentChatSession } from '~/api/agentChat'
import {
  deleteSession as deleteSessionAPI,
  getSession,
  getUserSessions,
} from '~/api/agentChat'
import { useAgentChatAPI } from '~/hooks/useAgentChatAPI'
import { useAgentChatSync } from '~/hooks/useAgentChatSync'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'
import { getCurrentUserId } from '~/store/reducers/auth'
import {
  InputMethod,
  trackEvent,
  TrackingEvent,
} from '~/utils/trackingProvider'

import type { FloatingPosition, FloatingSize } from './types'
import { ChatMode } from './types'

export { ChatMode } from './types'
export type { FloatingPosition, FloatingSize } from './types'

export enum ActionError {
  FAILED_TO_LOAD_SESSIONS = 'FAILED_TO_LOAD_SESSIONS',
  FAILED_TO_SELECT_SESSION = 'FAILED_TO_SELECT_SESSION',
  FAILED_TO_CREATE_SESSION = 'FAILED_TO_CREATE_SESSION',
  FAILED_TO_DELETE_SESSION = 'FAILED_TO_DELETE_SESSION',
  FAILED_TO_LOAD_SESSION = 'FAILED_TO_LOAD_SESSION',
}

const INITIAL_FLOATING_POSITION_Y = 72 // Below the header
const INITIAL_FLOATING_WIDTH = 600
const INITIAL_FLOATING_HEIGHT = 500
const SCROLLBAR_WIDTH = 16

interface AgentChatContextType {
  actionError: ActionError | null
  chatMode: ChatMode
  clearMessages: () => void
  closeChat: () => void
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
  createNewSession: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<boolean>
  dismissActionError: () => void
  error: string | null
  floatingPosition: FloatingPosition
  floatingSize: FloatingSize
  isChatOpen: boolean
  isLoading: boolean
  isLoadingSessions: boolean
  isResponding: boolean
  isRespondingInAnotherTab: boolean
  loadSessions: () => Promise<void>
  messages: AgentChatMessage[]
  openChat: () => void
  selectSession: (session: AgentChatSession) => Promise<void>
  sendMessage: (params: {
    content: string
    file?: { data: ArrayBuffer; name: string; type: string }
    inputMethod?: InputMethod
    promptMetadata?: Record<string, any>
  }) => Promise<void>
  session: AgentChatSession | null
  sessions: AgentChatSession[]
  setChatMode: (mode: ChatMode) => void
  startSession: () => Promise<void>
  toggleChatMode: () => void
  updateFloatingPosition: (position: FloatingPosition) => void
  updateFloatingSize: (size: FloatingSize) => void
}

const AgentChatContext = createContext<AgentChatContextType | undefined>(
  undefined,
)

interface AgentChatProviderProps {
  children: ReactNode
}

export function AgentChatProvider({ children }: AgentChatProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.FLOATING)
  const [sessions, setSessions] = useState<AgentChatSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionOpenedAt, setSessionOpenedAt] = useState<number | null>(null)
  const [actionError, setActionError] = useState<ActionError | null>(null)
  const [isRespondingInAnotherTab, setIsRespondingInAnotherTab] =
    useState(false)
  const [floatingPosition, setFloatingPosition] = useState<FloatingPosition>({
    x:
      typeof window !== 'undefined'
        ? window.innerWidth - INITIAL_FLOATING_WIDTH - SCROLLBAR_WIDTH
        : 400,
    y: INITIAL_FLOATING_POSITION_Y,
  })
  const [floatingSize, setFloatingSize] = useState<FloatingSize>({
    width: INITIAL_FLOATING_WIDTH,
    height:
      typeof window !== 'undefined'
        ? window.innerHeight - INITIAL_FLOATING_POSITION_Y * 2
        : INITIAL_FLOATING_HEIGHT,
  })

  const isAgentChatEnabled = useIsAgentChatEnabled()
  const currentUserId = useSelector(getCurrentUserId)

  const updateSession = useCallback((updatedSession: AgentChatSession) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === updatedSession.id ? updatedSession : s)),
    )
  }, [])

  const addSession = useCallback((newSession: AgentChatSession) => {
    setSessions((prev) => [newSession, ...prev])
  }, [])

  const {
    messages,
    loading: isLoading,
    isResponding,
    connectionState,
    sendMessage: hookSendMessage,
    startSession: hookStartSession,
    clearMessages,
    error,
    loadSession: hookLoadSession,
    session,
    updateMessagesFromSession,
  } = useAgentChatAPI({
    onSessionUpdate: updateSession,
    onSessionCreated: addSession,
  })

  const syncSessionFromAnotherTab = useCallback(
    async (sessionId: string) => {
      if (!currentUserId) return

      const capturedSessionId = session?.id

      try {
        const updatedSession = await getSession(currentUserId, sessionId)
        updateSession(updatedSession)

        if (capturedSessionId === sessionId && session?.id === sessionId) {
          updateMessagesFromSession(updatedSession)
        }
      } catch (caughtError) {
        // Silently fail - the other tab has the updated state
        // eslint-disable-next-line no-console
        console.warn('Failed to sync session from another tab:', caughtError)
      }
    },
    [currentUserId, session?.id, updateSession, updateMessagesFromSession],
  )

  const handleSessionUpdateFromAnotherTab = useCallback(
    async (sessionId: string) => {
      // Small delay to allow backend to persist the message
      await new Promise((resolve) => {
        setTimeout(resolve, 100)
      })

      await syncSessionFromAnotherTab(sessionId)
    },
    [syncSessionFromAnotherTab],
  )

  const handleIsRespondingUpdateFromAnotherTab = useCallback(
    async (responding: boolean, sessionId: string) => {
      setIsRespondingInAnotherTab(responding)

      // When another tab finishes responding, refetch to get the completed message
      if (!responding && session?.id === sessionId) {
        await syncSessionFromAnotherTab(sessionId)
      }
    },
    [session?.id, syncSessionFromAnotherTab],
  )

  // Sync chat state across browser tabs
  useAgentChatSync({
    session,
    messages,
    isResponding,
    onSessionUpdate: handleSessionUpdateFromAnotherTab,
    onIsRespondingUpdate: handleIsRespondingUpdateFromAnotherTab,
  })

  useEffect(() => {
    setIsRespondingInAnotherTab(false)
  }, [session?.id])

  const openChat = useCallback(() => {
    setIsChatOpen(true)
    setSessionOpenedAt(Date.now())
    trackEvent(TrackingEvent.AI_ASSISTANT_OPENED, {
      chat_mode: chatMode,
      agent_session_id: session?.id ?? null,
    })
  }, [chatMode, session?.id])

  const closeChat = useCallback(() => {
    setIsChatOpen(false)

    const sessionDuration =
      sessionOpenedAt !== null ? Date.now() - sessionOpenedAt : null
    const messageCount = messages
      ? messages.filter((msg) => !msg.isLoading).length
      : 0

    trackEvent(TrackingEvent.AI_ASSISTANT_CLOSED, {
      chat_mode: chatMode,
      session_window_open_duration_ms: sessionDuration,
      message_count: messageCount,
      agent_session_id: session?.id ?? null,
    })

    setSessionOpenedAt(null)
  }, [chatMode, messages, session?.id, sessionOpenedAt])

  const updateFloatingPosition = (position: FloatingPosition) => {
    setFloatingPosition(position)
  }

  const updateFloatingSize = (size: FloatingSize) => {
    setFloatingSize(size)
  }

  const toggleChatMode = () => {
    setChatMode((prev) =>
      prev === ChatMode.RAIL ? ChatMode.FLOATING : ChatMode.RAIL,
    )
  }

  const dismissActionError = () => {
    setActionError(null)
  }

  const loadSessions = useCallback(async () => {
    if (!currentUserId) return

    try {
      setIsLoadingSessions(true)
      const userSessions = await getUserSessions(currentUserId)
      setSessions(userSessions)
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_LOAD_SESSIONS)
    } finally {
      setIsLoadingSessions(false)
    }
  }, [currentUserId])

  const selectSession = useCallback(
    async (selectedSession: AgentChatSession) => {
      const previousSessionId = session?.id ?? null
      const messageCountInPrevious = messages
        ? messages.filter((msg) => !msg.isLoading).length
        : 0

      try {
        await hookLoadSession(selectedSession.id)

        trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_SWITCHED, {
          previous_agent_session_id: previousSessionId,
          new_agent_session_id: selectedSession.id,
          message_count_in_previous: messageCountInPrevious,
          chat_mode: chatMode,
        })
      } catch (caughtError) {
        setActionError(ActionError.FAILED_TO_SELECT_SESSION)
      }
    },
    [hookLoadSession, session?.id, messages, chatMode],
  )

  const createNewSession = useCallback(async () => {
    const isFirstSession = sessions ? sessions.length === 0 : true

    try {
      await hookStartSession()

      trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_CREATED, {
        is_first_session: isFirstSession,
        total_sessions: sessions ? sessions.length + 1 : 1,
        chat_mode: chatMode,
        is_auto_created: false,
      })
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_CREATE_SESSION)
    }
  }, [hookStartSession, sessions, chatMode])

  const deleteSession = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        if (!currentUserId) return false
        await deleteSessionAPI(currentUserId, sessionId)
        setSessions((prev) => prev.filter((s) => s.id !== sessionId))
        trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_DELETED, {
          agent_session_id: sessionId,
        })
        return true
      } catch (caughtError) {
        setActionError(ActionError.FAILED_TO_DELETE_SESSION)
        return false
      }
    },
    [currentUserId],
  )

  const sendMessage = useCallback(
    async ({
      content,
      inputMethod = InputMethod.TYPING,
      promptMetadata,
      file,
    }: {
      content: string
      file?: { data: ArrayBuffer; name: string; type: string }
      inputMethod?: InputMethod
      promptMetadata?: Record<string, any>
    }) => {
      await hookSendMessage({
        content,
        inputMethod,
        promptMetadata,
        chatMode,
        file,
      })
    },
    [chatMode, hookSendMessage],
  )

  const loadMostRecentSession = useCallback(async () => {
    if (!currentUserId) return

    try {
      const userSessions = await getUserSessions(currentUserId)
      setSessions(userSessions)

      if (userSessions.length > 0) {
        const sortedSessions = [...userSessions].sort(
          (a, b) => b.lastUpdateTime - a.lastUpdateTime,
        )
        await hookLoadSession(sortedSessions[0].id)
      }
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_LOAD_SESSION)
    }
  }, [currentUserId, hookLoadSession])

  // Auto-load most recent session on mount
  useEffect(() => {
    if (!isAgentChatEnabled || !currentUserId || session) return
    loadMostRecentSession()
  }, [isAgentChatEnabled, currentUserId, session, loadMostRecentSession])

  // Ensure session is loaded when chat opens (if mount load was skipped)
  useEffect(() => {
    if (!isAgentChatEnabled || !currentUserId || !isChatOpen || session) return
    loadMostRecentSession()
  }, [
    isAgentChatEnabled,
    currentUserId,
    isChatOpen,
    session,
    loadMostRecentSession,
  ])

  useEffect(() => {
    if (isChatOpen && isAgentChatEnabled && chatMode === ChatMode.RAIL) {
      document.body.classList.add('agent-chat-rail-open')
    } else {
      document.body.classList.remove('agent-chat-rail-open')
    }

    return () => {
      document.body.classList.remove('agent-chat-rail-open')
    }
  }, [isChatOpen, isAgentChatEnabled, chatMode])

  const value = React.useMemo<AgentChatContextType>(
    () => ({
      isChatOpen,
      openChat,
      closeChat,
      chatMode,
      setChatMode,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      isRespondingInAnotherTab,
      connectionState,
      sendMessage,
      startSession: hookStartSession,
      clearMessages,
      error,
      actionError,
      dismissActionError,
      session,
      sessions,
      loadSessions,
      selectSession,
      createNewSession,
      deleteSession,
      isResponding,
    }),
    [
      isChatOpen,
      openChat,
      closeChat,
      chatMode,
      setChatMode,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      isRespondingInAnotherTab,
      connectionState,
      sendMessage,
      hookStartSession,
      clearMessages,
      error,
      actionError,
      dismissActionError,
      session,
      sessions,
      loadSessions,
      selectSession,
      createNewSession,
      deleteSession,
      isResponding,
    ],
  )

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  )
}

export function useAgentChat() {
  const context = useContext(AgentChatContext)
  if (context === undefined) {
    throw new Error('useAgentChat must be used within an AgentChatProvider')
  }
  return context
}
